/* eslint-disable max-lines-per-function */
import _ from 'lodash';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { TaskStatus, TaskType } from '../../enums';
import type { GetTaskListRep } from '../../services/task-repository';
import type { TaskBasic, TaskItem, TaskPagination, TaskQueryParams } from '../../types';
import { TaskDateFormatter } from '@/services/date-format/task-date-formatter';
import { TaskApi, type GetTaskListRequest, type GetTaskListResponse } from '../../apis/task-api';

@injectable()
export class GetTaskListRepImpl implements GetTaskListRep {
    constructor(
        @inject(TaskApi)
        private _taskApi: TaskApi,
        @inject(TaskDateFormatter)
        private readonly _taskDateFormatter: TaskDateFormatter,
    ) {}

    async fetch(params: TaskQueryParams): Promise<TaskBasic> {
        const result = await this._taskApi.getTaskList({
            page: params.page,
            limit: params.pageSize,
            type: this._convertEnumToApiType(params.taskType),
        });
        const tasks: TaskItem[] = _.map(result.list, (item) => {
            return {
                id: item.task_id,
                title: item.title,
                createTime: this._taskDateFormatter.format(item.created_at),
                updateTime: this._taskDateFormatter.format(item.updated_at),
                taskType: this._convertApiTypeToEnum(item.type),
                status: this._convertApiStatusToEnum(item.task_status),
                readStatus: item.read,
            };
        });

        const pagination: TaskPagination = {
            total: result.total,
            page: result.page,
            pageSize: result.limit,
        };

        return {
            tasks,
            pagination,
        };
    }

    private _convertApiStatusToEnum(task_status: GetTaskListResponse['list'][number]['task_status']): TaskStatus {
        if (task_status === 'Ready') {
            return TaskStatus.READY;
        }
        else if (task_status === 'Running') {
            return TaskStatus.PROCESSING;
        }
        else if (task_status === 'Complete') {
            return TaskStatus.COMPLETED;
        }
        else if (task_status === 'Failed') {
            return TaskStatus.FAILED;
        }
        else if (task_status === 'Interrupt') {
            return TaskStatus.INTERRUPTED;
        }
        else {
            throw new Error('Invalid task status, please check the api response');
        }
    }

    // eslint-disable-next-line complexity
    private _convertEnumToApiType(taskType?: TaskType): GetTaskListRequest['type'] {
        if (!taskType) {
            return 'ALL';
        }
        else if (taskType === TaskType.TRANSLATION) {
            return 'AI_TRANSLATION';
        }
        else if (taskType === TaskType.DISCLOSURE) {
            return 'AI_PATENT_DISCLOSURE';
        }
        else if (taskType === TaskType.SPECIFICATION) {
            return 'AI_SPECIFICATION';
        }
        else if (taskType === TaskType.NOVELTY_SEARCH) {
            return 'AI_NOVELTY_SEARCH';
        }
        else if (taskType === TaskType.FTO_SEARCH) {
            return 'AI_FTO_SEARCH';
        }
        else if (taskType === TaskType.NEW_FTO_SEARCH) {
            return 'AI_NEW_FTO_SEARCH';
        }
        else if (taskType === TaskType.AI_OAR) {
            return 'AI_OAR';
        }
        else {
            throw new Error('Invalid task type, please check the api response');
        }
    }

    // eslint-disable-next-line complexity
    private _convertApiTypeToEnum(apiType: GetTaskListRequest['type']): TaskType {
        if (apiType === 'AI_TRANSLATION') {
            return TaskType.TRANSLATION;
        }
        else if (apiType === 'AI_PATENT_DISCLOSURE') {
            return TaskType.DISCLOSURE;
        }
        else if (apiType === 'AI_SPECIFICATION') {
            return TaskType.SPECIFICATION;
        }
        else if (apiType === 'AI_NOVELTY_SEARCH') {
            return TaskType.NOVELTY_SEARCH;
        }
        else if (apiType === 'AI_FTO_SEARCH') {
            return TaskType.FTO_SEARCH;
        }
        else if (apiType === 'AI_NEW_FTO_SEARCH') {
            return TaskType.NEW_FTO_SEARCH;
        }
        else if (apiType === 'AI_OAR') {
            return TaskType.AI_OAR;
        }
        else {
            throw new Error('Invalid task type, please check the api response');
        }
    }
}
